import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import { cn } from '@rocco/utils/cn';
import {
    ISLAND_GLASS_DARK,
    ISLAND_GLASS_LAYOUT_ID,
} from '~/ui-rocco/pages/detail-shared/magic-island/styles';

import { countLabel } from './bulk-enquiry-copy';
import type { BulkEnquiryItem } from './bulk-enquiry-state';

const POP = { type: 'spring', stiffness: 520, damping: 24, mass: 1 } as const;

interface BulkEnquireExitConfirmIslandProps {
    items: BulkEnquiryItem[];
    /** Stay in bulk mode with the selection intact. */
    onKeep: () => void;
    /** Exit bulk mode and clear the selection. */
    onDiscard: () => void;
}

/**
 * The island's inline exit confirmation — shown in place of the selection pill
 * (same glass, shared layoutId, so the pill morphs between the two) when the
 * user tries to exit with items still in the list. The floating label carries
 * the question; the pill holds the two answers, with "Keep selecting" as the
 * safe, focused default. Escape keeps the selection too.
 */
export const BulkEnquireExitConfirmIsland = ({
    items,
    onKeep,
    onDiscard,
}: BulkEnquireExitConfirmIslandProps) => {
    const reduce = useReducedMotion() ?? false;
    const pop = reduce ? { duration: 0 } : POP;

    // Focus the safe action so Enter keeps the list; Escape backs out too.
    const keepRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        keepRef.current?.focus({ preventScroll: true });
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onKeep();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onKeep]);

    return (
        <div className="pointer-events-auto flex items-center justify-center max-md:w-screen">
            <div className="relative">
                <motion.span
                    initial={reduce ? false : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={pop}
                    role="alert"
                    className="absolute bottom-full left-1/2 mb-3 -translate-x-1/2 rounded-full bg-white px-3 py-1.5 font-sans text-sm font-medium whitespace-nowrap text-[#181818] shadow-[0_6px_20px_rgba(0,0,0,0.18)]"
                >
                    {`Discard ${countLabel(items)}?`}
                </motion.span>

                <motion.div
                    layout={!reduce}
                    layoutId={reduce ? undefined : ISLAND_GLASS_LAYOUT_ID}
                    transition={pop}
                    className={cn(
                        ISLAND_GLASS_DARK,
                        'flex h-14 items-center gap-1.5 rounded-full p-2',
                    )}
                >
                    <button
                        type="button"
                        onClick={onDiscard}
                        className="flex h-10 items-center rounded-full px-4 font-sans text-base font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white max-md:h-11"
                    >
                        Discard
                    </button>
                    <button
                        ref={keepRef}
                        type="button"
                        onClick={onKeep}
                        className="flex h-10 items-center rounded-full bg-white px-4 font-sans text-base font-medium text-[#181818] transition-transform duration-200 ease-out active:scale-95 max-md:h-11"
                    >
                        Keep selecting
                    </button>
                </motion.div>
            </div>
        </div>
    );
};
